import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApproveOrdersModal from "../../ApproveOrdersModal/ApproveOrdersModal";
import Footer from "../../../components/nav-bar/Footer";
import moment from "moment";
import {
  getArchivedBookDetails,
  getUserBookDetails,
  getviewUserDetails,
} from "../../../api/userProfile";
import { ArchiveBookAPI, UnarchiveBookAPI, UpdateReviewAPI } from "../../../api/book";
import { useToast } from "../../../ToastContext";
import * as Role from "../../../Entities/Role";
import * as Status from "../../../Entities/RoleStatus";
import * as Order_Status from "../../../Entities/Order-Status";
import { FiEye } from "react-icons/fi";
import PDF from "../../../assets/images/PDF.svg";
import { getStoredUserId } from "../../../utils/session";
import { canArchiveBook, canEditStoryBook, isDraftBook } from "../../../utils/bookAccess";

function AdminDashboard() {
  const navigate = useNavigate();
  const toast = useToast();
  const [selectAll, setSelectAll] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userViewDetails, setVieUserDetails] = useState(null);
  const [status, setStatus] = useState("");
  const userId = getStoredUserId();
  const roleId = localStorage.getItem("RoleId");
  const [data, setData] = useState([]);
  const [archivedData, setArchivedData] = useState([]);
  const [activeTab, setActiveTab] = useState("Created");
  const [pdfSelect, setPdfSelect] = useState(null);
  const fetchUserDetails = useCallback(async () => {
    try {
      const response = await getUserBookDetails(userId, false);
      if (response && response.data) {
        setData(response.data);
      } else {
        console.error("No data found in response.");
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  }, [userId]);

  const fetchArchivedDetails = useCallback(async () => {
    try {
      const response = await getArchivedBookDetails();
      if (response && response.data) {
        setArchivedData(Array.isArray(response.data) ? response.data : []);
      } else {
        console.error("No data found in response.");
      }
    } catch (error) {
      console.error("Error fetching archived books:", error);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
      fetchArchivedDetails();
    }
  }, [userId, fetchUserDetails, fetchArchivedDetails]);

  const displayedData = activeTab === "Created" ? data : archivedData;
  const canSelectOrders = activeTab === "Created";

  const handleSelectAll = () => {
    if (!canSelectOrders) {
      return;
    }
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    const newSelectedOrders = newSelectAll
      ? displayedData.reduce((acc, order) => {
          if (!isDraftBook(order)) {
            acc[order.Id] = true;
          }
          return acc;
        }, {})
      : {};
    setSelectedOrders(newSelectedOrders);
  };

  const handleSelectOrder = (orderId) => {
    if (!canSelectOrders) {
      return;
    }
    const order = displayedData.find((item) => item.Id === orderId);
    if (order && isDraftBook(order)) {
      return;
    }
    setSelectedOrders((prevState) => {
      const newState = { ...prevState };
      if (newState[orderId]) {
        delete newState[orderId];
      } else {
        newState[orderId] = true;
      }
      return newState;
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const handleApprove = async () => {
    if (!canSelectOrders) {
      return;
    }
    const bookIds = Object.keys(selectedOrders).filter(
      (id) => selectedOrders[id]
    );
    const bookId = bookIds[0];
    const newStatus = roleId === Role.ADMIN ? Status.REVIEWE : Status.APPROVED;
    const payload = {
      userId: userId,
      bookId: parseInt(bookId),
      reviewStatus: newStatus,
      reason: null,
    };
    try {
      const response = await UpdateReviewAPI(payload);
      if (response && response.data && response.status === 200) {
        setSelectedOrders({});
        setSelectAll(false);
        await fetchUserDetails();
        toast.success("Approved updated successfully");
      } else {
        toast.error("Failed to update Approve");
      }
    } catch (error) {
      console.error("Error updating Approve:", error);
      toast.error("An error occurred while updating the Approve");
    }
  };

  const handleArchive = async (order) => {
    const payload = { bookId: order.Id };
    try {
      const response = await ArchiveBookAPI(payload);
      if (response && response.status === 200) {
        await fetchUserDetails();
        await fetchArchivedDetails();
        toast.success("Book archived successfully");
      } else {
        toast.error("Failed to archive book");
      }
    } catch (error) {
      console.error("Error archiving book:", error);
      toast.error(
        error?.response?.data?.message ||
          "An error occurred while archiving the book"
      );
    }
  };

  const handleUnarchive = async (order) => {
    const payload = { bookId: order.Id };
    try {
      const response = await UnarchiveBookAPI(payload);
      if (response && response.status === 200) {
        await fetchUserDetails();
        await fetchArchivedDetails();
        toast.success("Book unarchived successfully");
      } else {
        toast.error("Failed to unarchive book");
      }
    } catch (error) {
      console.error("Error unarchiving book:", error);
      toast.error(
        error?.response?.data?.message ||
          "An error occurred while unarchiving the book"
      );
    }
  };

  const handleClick = async (user) => {
    try {
      const response = await getviewUserDetails(user);
      if (response && response.data) {
        setIsModalOpen(true);
        setVieUserDetails(response.data);
      } else {
        console.error("No data found in response.");
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const handleNavigateBook = async (bookId) => {
    try {
      const response = await getviewUserDetails(bookId);
      if (response && response.data) {
        localStorage.setItem("userData", JSON.stringify(response.data));
        navigate("/form", { state: { userData: response.data } });
      } else {
        console.error("No data found in response.");
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case Status.WAITING_APPROVAL:
        return {
          text: Order_Status.WAITING_APPROVAL,
          color: "bg-orange-100 text-orange-600",
        };
      case Status.ACTIVE:
        return {
          text: Order_Status.ACTIVE,
          color: "bg-green-100 text-green-600",
        };
      case Status.INACTIVE:
        return {
          text: Order_Status.INACTIVE,
          color: "bg-gray-100 text-gray-600",
        };
      case Status.REJECTED:
        return {
          text: Order_Status.REJECTED,
          color: "bg-red-100 text-red-600",
        };
      case Status.REVIEWE:
        return {
          text: Order_Status.REVIEWE,
          color: "bg-blue-100 text-blue-600",
        };
      case Status.PRINTING:
        return {
          text: Order_Status.PRINTING,
          color: "bg-green-100 text-green-600",
        };
      case Status.SHIPPING:
        return {
          text: Order_Status.SHIPPING,
          color: "bg-green-100 text-green-600",
        };
      case Status.APPROVED:
        return {
          text: Order_Status.APPROVED,
          color: "bg-green-100 text-green-600",
        };
      case Status.DRAFT:
        return {
          text: Order_Status.DRAFT,
          color: "bg-red-100 text-red-600",
        };
      default:
        return {
          text: "-",
        };
    }
  };
  const handleDemoPDFClick = (order) => {
    const pdfUrl = order.PDFFileUrl;
    console.log(order.PDFFileUrl, "pdf");
    setPdfSelect(order.PDFFileUrl);
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    } else {
      console.error("PDF URL not found");
    }
  };

  return (
    <div className="w-full h-screen flex items-center flex-col bg-white">
      <div className="text-left w-full mt-10 px-16">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-normal text-customGreyBase font-Merriweather">
          Dashboard
        </h1>
      </div>
      <div className="w-11/12 sm:w-10/12 md:w-9/12 lg:w-11/12 flex align-middle justify-center mt-10">
        <div className="border border-gray-300 rounded-lg shadow-sm w-full">
          <div className="flex flex-col sm:flex-row justify-between px-4 py-3">
            <div className="flex flex-row items-center space-x-4">
              <button
                onClick={() => {
                  setActiveTab("Created");
                  setSelectedOrders({});
                  setSelectAll(false);
                }}
                className={`pb-2 border-b-2 ${
                  activeTab === "Created"
                    ? "border-customGreen text-customGreen font-semibold"
                    : "border-transparent text-gray-500"
                }`}
              >
                Created Books
              </button>
              <button
                onClick={() => {
                  setActiveTab("Archived");
                  setSelectedOrders({});
                  setSelectAll(false);
                }}
                className={`pb-2 border-b-2 ${
                  activeTab === "Archived"
                    ? "border-customGreen text-customGreen font-semibold"
                    : "border-transparent text-gray-500"
                }`}
              >
                Archived Books
              </button>
            </div>
          </div>
          <div className="overflow-y-auto max-h-80 border border-gray-200 rounded-lg">
            <table className="min-w-full table-fixed border-collapse">
              <thead className="bg-white sticky top-0 z-10 border-b border-gray-300 shadow-sm">
                <tr>
                  {canSelectOrders && (
                    <th className="py-3 px-4 text-left font-medium text-gray-600 w-[5%] border-gray-300">
                      <input
                        type="checkbox"
                        className="rounded-sm"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th>
                  )}
                  <th className="py-3 px-4 text-left font-semibold text-gray-500 w-[15%] border-gray-300">
                    Book ID
                  </th>
                  <th className="py-3 px-4 text-left font-semibold text-gray-500 w-[20%] border-gray-300">
                    Customer Name
                  </th>
                  <th className="py-3 px-4 text-left font-semibold text-gray-500 w-[20%] border-gray-300">
                    Book Title
                  </th>
                  <th className="py-3 px-4 text-left font-semibold text-gray-500 w-[15%] border-gray-300">
                    Order Date
                  </th>
                  <th className="py-3 px-4 text-left font-semibold text-gray-500 w-[15%] border-gray-300">
                    Status
                  </th>
                  <th className="py-3 px-4 text-left font-semibold text-gray-500 w-[15%] border-gray-300">
                    Book
                  </th>
                  <th className="py-3 px-4 text-left font-semibold text-gray-500 w-[10%]">
                    View
                  </th>
                  <th className="py-3 px-4 text-left font-semibold text-gray-500 w-[10%]">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {displayedData.length > 0 ? (
                  displayedData.map((order) => (
                    <tr className="border-b border-gray-200" key={order.Id}>
                      {canSelectOrders && (
                        <td className="py-2 px-4">
                          <input
                            type="checkbox"
                            className="rounded-sm"
                            checked={!!selectedOrders[order.Id]}
                            disabled={isDraftBook(order)}
                            onChange={() => handleSelectOrder(order.Id)}
                          />
                        </td>
                      )}
                      <td className="py-2 px-4 text-gray-700 font-normal border-gray-300">
                        {order.Id}
                      </td>
                      <td className="py-2 px-4 text-gray-700 font-normal border-gray-300">
                        {order.CustomerName}
                      </td>
                      <td className="py-2 px-4 text-gray-700 font-normal border-gray-300">
                        {order.Name}
                      </td>
                      <td className="py-2 px-4 text-gray-700 font-normal border-gray-300">
                        {moment(order.CreatedDateTime).format("MM/DD/YYYY")}
                      </td>
                      <td className="py-2 px-4 font-normal border-gray-300">
                        <span
                          className={`${
                            getStatusStyle(
                              order.Status === Status.DRAFT
                                ? Status.DRAFT
                                : order.ReviewStatus
                            ).color
                          } py-1 px-3 rounded-full sm:text-sm text-xs`}
                        >
                          {getStatusStyle(
                            order.Status === Status.DRAFT
                              ? Status.DRAFT
                              : order.ReviewStatus
                          ).text || "-"}
                        </span>
                      </td>
                      <td className="py-2 sm:text-sm text-xs sm:py-3 px-4">
                        {order.PDFFileUrl ? (
                          <img
                            src={PDF}
                            alt="pdf"
                            width={"30px"}
                            height={"30px"}
                            className="cursor-pointer"
                            onClick={() => handleDemoPDFClick(order)}
                          />
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="py-2 px-4 text-gray-700 font-normal">
                        <span
                          className="text-gray-600 cursor-pointer"
                          onClick={() => handleClick(order.Id)}
                        >
                          View
                        </span>
                      </td>
                      <td className="py-2 px-4 text-gray-700 font-normal">
                        {activeTab === "Created" ? (
                          <div className="flex flex-col gap-1">
                            {canEditStoryBook(roleId, order) && (
                              <span
                                className="text-gray-600 cursor-pointer"
                                onClick={() => handleNavigateBook(order.Id)}
                              >
                                Edit
                              </span>
                            )}
                            {canArchiveBook(roleId, order) && (
                              <span
                                className="text-red-600 cursor-pointer"
                                onClick={() => handleArchive(order)}
                              >
                                Archive
                              </span>
                            )}
                          </div>
                        ) : (
                          <span
                            className="text-customGreen cursor-pointer"
                            onClick={() => handleUnarchive(order)}
                          >
                            Unarchive
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="w-full">
                    <td
                      colSpan={canSelectOrders ? 9 : 8}
                      className="py-4 text-center text-gray-500 text-sm"
                    >
                      No orders
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Modal */}
      <ApproveOrdersModal
        fetchUserDetails={fetchUserDetails}
        status={status}
        setStatus={setStatus}
        userViewDetails={userViewDetails}
        isOpen={isModalOpen}
        closeModal={closeModal}
        handleApprove={handleApprove}
      />
      <div className="mt-44 w-full">
        <Footer />
      </div>
    </div>
  );
}

export default AdminDashboard;
